import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

// import { ReactComponent as TrendDownIcon } from '../../../../assets/icons/trend-down.svg';
// import { ReactComponent as TrendUpIcon } from '../../../../assets/icons/trend-up.svg';
import { useWebSocket } from '../../../../contexts/WebSocketV2/WebSocketProvider';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  setSingleLocationPerformance,
  setLocationPerformance,
  fetchAvgOnlineOfflineDetails,
} from '../../../../redux/slices/LocationManagementSidePanelSlice';
import { RootState } from '../../../../redux/store';
import SelectField from '../../../../shared-components/select-field/SelectField';
import TabToggle, { TabItem } from '../../../../shared-components/tab-toggle/TabToggle';
import stationManagement from '../../../../utils/api/location-management';
import ConnectorStatusDistributionChart from '../LocationPerformance/ConnectorStatusDistributionChart';
import HardwareStats from '../LocationPerformance/HardwareStats';
// import HealthIndexTrendChart from '../LocationPerformance/HealthIndexTrendChart';
import HeatMapByHour from '../LocationPerformance/HeatMapByHour';
import HistoricalFaults from '../LocationPerformance/HistoricalFaults';
import LocationStatusDistributionChart from '../LocationPerformance/LocationStatusDistributionChart';
import SoftWareLogs from '../LocationPerformance/SoftWareLogList';
import UptimeLocationChart from '../LocationPerformance/UptimeLocationChart';

// import MeterChart from './MeterChart';

/**
 * Station performance component
 * @returns {any} The rendered component
 */
function LocationPerformance() {
  const { t, i18n } = useTranslation('sidePanel');
  const { connectWebSocket } = useWebSocket();
  const { messages } = useAppSelector((state) => state.ocppMessageV2);
  const { id: urlStationId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { selectedChargingPoint /* onlineAvgHours, offlineTotalHours */ } = useAppSelector(
    (state: RootState) => state.locationManagement,
  );
  const [selectedTab, setSelectedTab] = useState<string>('softwareLogs');
  const [deviceOptions, setDeviceOptions] = useState<{ label: string; value: string }[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string | number>('');

  useEffect(() => {
    const stationId = urlStationId || selectedChargingPoint?.id || 'all';
    dispatch(
      setLocationPerformance({
        id: stationId,
      }),
    );
    dispatch(
      fetchAvgOnlineOfflineDetails({
        chargingStation: stationId,
      }),
    );
  }, [urlStationId, selectedChargingPoint?.id]);
  /**
   * connect web Socket
   */
  const handleConnect = () => {
    if (selectedDevice) {
      connectWebSocket(selectedDevice); // automatically disconnects old and connects new
    }
  };

  /**
   * Handle dropdown change
   * @param {string} _name The field name
   * @param {any} sn The selected value
   */
  const handleChangeDevice = (_name: string, sn: string | number | (string | number)[]) => {
    const selected = Array.isArray(sn) ? sn[0] : sn;
    setSelectedDevice(selected);
  };

  /**
   * Fetch device list
   */
  const handleDevicesList = async () => {
    if (!selectedChargingPoint) return;

    try {
      const response = await stationManagement.fetchDeviceList({
        chargingStation: selectedChargingPoint?.id,
      });

      const list = response.data || [];
      const formatted = list.map((item: any) => ({
        label: item.chargingPointName || item.chargingPointSn || t('unnamedDevice'),
        value: item.chargingPointSn,
      }));

      setDeviceOptions(formatted);

      // Auto-select first device
      if (formatted.length > 0) {
        handleChangeDevice('value', formatted[0].value);
      }
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        },
      });
    }
  };

  useEffect(() => {
    handleDevicesList();
  }, [selectedChargingPoint]);
  useEffect(() => {
    handleConnect();
  }, [selectedDevice]);

  useEffect(() => {
    dispatch(
      setSingleLocationPerformance({
        id: selectedDevice,
      }),
    );
  }, [selectedDevice]);

  /**
   * Convert websocket messages into connector slides
   */
  const connectorSlides = messages
    .filter((message: any) => message.event === 'RealTime Charging Monitoring')
    .map((message: any) => ({
      chargingSpeed: message.chargingSpeed || 0,
    }));

  const tabItems: TabItem[] = useMemo(
    () => [
      { label: t('softwareLogs'), value: 'softwareLogs' },
      { label: t('historicalFaults'), value: 'historicalFaults' },
    ],
    [t],
  );

  return (
    <div className="location-performance-container" dir={i18n.dir()}>
      {/* <div className="location-performance-heading">
        <div className="time-content">
          <div className="time-card uptime">
            <div className="icon-box">
              <TrendUpIcon />
            </div>
            <div className="text-box">
              <p className="time-value">
                {onlineAvgHours} {t('hrs')}
              </p>
              <p className="time-label">{t('avgUptimeAcrossAllStations')}</p>
            </div>
          </div>
          <div className="time-card downtime">
            <div className="icon-box">
              <TrendDownIcon />
            </div>
            <div className="text-box">
              <p className="time-value">
                {offlineTotalHours} {t('hr')}
              </p>
              <p className="time-label">{t('totalDowntimeHoursThisMonth')}</p>
            </div>
          </div>
        </div>
      </div> */}

      <div className="chart-container">
        {/* <HealthIndexTrendChart /> */}
        <div>
          <LocationStatusDistributionChart />
        </div>
        <div>
          <ConnectorStatusDistributionChart />
        </div>
        <div>
          <UptimeLocationChart />
        </div>
        <div>
          {' '}
          <HeatMapByHour />
        </div>
      </div>

      <div className="tab-toggle-heading">
        <TabToggle
          tabs={tabItems}
          isLabelValue
          activeTab={selectedTab}
          defaultTab="softwareLogs"
          onChange={(tab) => setSelectedTab(tab)}
        />
      </div>
      <div className="select-field">
        <SelectField
          options={deviceOptions}
          placeholder={t('selectDevice')}
          selectedValue={selectedDevice}
          name="selectedStation"
          onChangeValue={handleChangeDevice}
        />
      </div>

      {selectedTab === '' && (
        <div className="charging-voltage-card-wrap">
          <div className="location-status-distributionChart-wrap" dir={i18n.dir()}>
            <div className="energy-report-container">
              <div className="report-header">
                <div className="report-title">
                  <span className="title-text">{t('chargingVoltage')}</span>
                  <p className="subtitle-text">{t('chargingVoltageDesc')}</p>
                </div>
              </div>
              <div
                className="heatmap-by-hours-container"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '280px',
                  boxSizing: 'border-box',
                }}>
                <div style={{ width: '265px', height: '160px', position: 'relative' }}>
                  <HardwareStats
                    value={connectorSlides[0]?.chargingSpeed || 0.0}
                    label={t('chargingVoltage')}
                    units={` ${t('voltage')}`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedTab === 'softwareLogs' && (
        <div className="soft-ware-logs">
          <SoftWareLogs />
        </div>
      )}

      {selectedTab === 'historicalFaults' && <HistoricalFaults />}
    </div>
  );
}

export default LocationPerformance;
